import '../shared/model/global';
import { documentCard } from '@/shared/model/document-card.model';
import { BriefingResourceRequest } from '@/shared/model/briefing-resource.model';
import markdownit from 'markdown-it';
import { IBriefingPage, IScrapAgent } from '@/shared/model/briefing-widget.model';
import { DUE_DATE_MAKE_A_THON, FILE_TYPES_ENUM, REFERENCE_FILE_TYPE, SVG_FILE } from '@/constants';
import * as config from '@/shared/config/config';
import Vue from 'vue';

const store = config.initVueXStore(Vue);

export async function getImageSizeByUrl(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () =>
      resolve({
        width: img.width,
        height: img.height,
      });
    img.onerror = reject;
    img.src = src;
  });
}

export function handleQueryIncludeArray(query) {
  // this will process params
  return Object.entries(query)
    .map(([key, value], i) => (Array.isArray(value) ? `${key}=${value.join('&' + key + '=')}` : `${key}=${value}`))
    .join('&');
}

export function isScriptLoaded(url) {
  if (!url) return false;
  const scripts = document.getElementsByTagName('script');
  for (let i = scripts.length; i--; ) {
    if (scripts[i].src == url) return true;
  }
  return false;
}

export function appendBaseParamRequest(page = 0, size = 10, sortBy = 'id', sortDirection = 'desc') {
  const mapReq = new Map();
  mapReq.set('page', page);
  mapReq.set('size', size);
  mapReq.set('sortBy', sortBy);
  mapReq.set('sortDirection', sortDirection);
  return appendParamToRequest(mapReq);
}

export function appendParamToRequest(mapParam: Map<string, any>, isContinue?): string {
  if (!mapParam) {
    return;
  }
  let paramUrl = isContinue ? '&' : '?';
  const keys = Array.from(mapParam.keys());
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    paramUrl = paramUrl + `${key}=` + mapParam.get(key);
    if (i < keys.length - 1) {
      paramUrl = paramUrl + '&';
    }
  }
  return paramUrl;
}

export function getExtensionFileByName(fileName: string) {
  if (!fileName) {
    return;
  }
  return fileName.split('.').pop();
}

/*
 * find all element duplicate in array
 * return: new array contain element duplicated
 * */
export function findDuplicates(arr) {
  if (!arr || arr.length == 0) {
    return;
  }
  const sorted_arr = arr.slice().sort(); // You can define the comparing function here.
  // JS by default uses a crappy string compare.
  const results = [];
  for (let i = 0; i < sorted_arr.length - 1; i++) {
    if (sorted_arr[i + 1] == sorted_arr[i]) {
      results.push(sorted_arr[i]);
    }
  }
  return results;
}

export function precise(x: any) {
  try {
    return Number.parseFloat(x).toPrecision(4);
  } catch (error) {
    return x;
  }
}

export function buildRequestParamBase(pageable) {
  return {
    page: pageable && pageable.page ? pageable.page : 0,
    size: pageable && pageable.size ? pageable.size : 10,
    sort: pageable && pageable.sort ? pageable.sort : ['createdDate,desc'],
  };
}

export function splitTagHtml(content): [] {
  if (!content) {
    return;
  }
  const div = document.createElement('div');
  div.innerHTML = content;

  div.querySelectorAll('span').forEach(span => {
    div.innerHTML = div.innerHTML.replace(span.outerHTML, '⠀' + span.outerHTML + '⠀'); // Invisible character U+2800
  });

  div.querySelectorAll('strong').forEach(strong => {
    div.innerHTML = div.innerHTML.replace(strong.outerHTML, '⠀' + strong.outerHTML + '⠀'); // Invisible character U+2800
  });

  content = div.innerHTML.split('⠀'); // U+2800
  return content.filter(x => x);
}

export function extractContentFromTag(s) {
  const span = document.createElement('span');
  span.innerHTML = s;
  return span.textContent || span.innerText;
}

export function getExtensionFileBySearchType(searchType: string) {
  if (!searchType) {
    return;
  }
  const fileType = searchType.split(':').pop();
  return fileType;
}

export function onlyInLeft(leftValue, rightValue, keyCompare: string[]) {
  const res = [];

  for (let i = 0; i < leftValue.length; i++) {
    let j = 0;
    let isSame = false;
    while (j < rightValue.length) {
      let m = 0;
      for (let n = 0; n < keyCompare.length; n++) {
        if (rightValue[j][keyCompare[n]] == leftValue[i][keyCompare[n]]) {
          m++;
        }
      }
      if (m == keyCompare.length) {
        isSame = true;
        break;
      }
      j++;
    }
    if (!isSame) res.push(leftValue[i]);
  }
  return res;
}

export function formatBytes(bytes, decimals = 2) {
  if (!+bytes) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

export function timeDifference(current, previous) {
  const msPerMinute = 60 * 1000;
  const msPerHour = msPerMinute * 60;
  const msPerDay = msPerHour * 24;
  const msPerMonth = msPerDay * 30;
  const msPerYear = msPerDay * 365;

  const elapsed = current - previous;
  if (elapsed <= 0) return '0 seconds ago';
  if (elapsed < msPerMinute) {
    return Math.round(elapsed / 1000) + ' seconds ago';
  } else if (elapsed < msPerHour) {
    return Math.round(elapsed / msPerMinute) + ' minutes ago';
  } else if (elapsed < msPerDay) {
    return Math.round(elapsed / msPerHour) + ' hours ago';
  } else if (elapsed < msPerMonth) {
    if (Math.round(elapsed / msPerDay / 7) > 1) return Math.round(elapsed / msPerDay / 7) + ' weeks ago';
    return Math.round(elapsed / msPerDay) + ' days ago';
  } else if (elapsed < msPerYear) {
    return Math.round(elapsed / msPerMonth) + ' months ago';
  } else {
    return Math.round(elapsed / msPerYear) + ' years ago';
  }
}

export function getDomainFromUrl(url) {
  try {
    const domain = new URL(url);
    return domain.hostname;
  } catch {
    return '';
  }
}

export async function toDataURL(url, options: any = {}) {
  try {
    const { timeout = 3000 } = options;
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);
    const reponse = await fetch(url, {
      ...options,
      signal: controller.signal,
    })
      .then(response => {
        if (response.status === 200) return response.blob();
        return null;
      })
      .then(
        blob =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          })
      )
      .catch(err => {
        return null;
      });
    clearTimeout(id);
    return reponse;
  } catch (error) {
    console.log(error.name === 'AbortError');
  }
}

export function detectFile(title, searchType?) {
  let path = '';
  const fileExtention = [
    { key: '.pdf', value: 'pdf' },
    { key: '.doc', value: 'word' },
    { key: '.docx', value: 'word' },
    { key: '.xls', value: 'excel' },
    { key: '.xlsx', value: 'excel' },
    { key: '.jpg', value: 'image' },
    { key: '.jpeg', value: 'image' },
    { key: '.png', value: 'image' },
    { key: '.txt', value: 'text' },
    { key: '.zip', value: 'zip' },
    { key: '.ppt', value: 'ppt' },
    { key: '.pptx', value: 'ppt' },
  ];
  let i = 0;
  while (i < fileExtention.length) {
    if (
      title.toLowerCase().endsWith(fileExtention[i].key) ||
      fileExtention[i].key.replace('.', '').indexOf(searchType?.toLowerCase()) > -1
    ) {
      path = `/content/images/common/file/file-${fileExtention[i].value}-fill.svg`;
      break;
    }
    i++;
  }
  if (!path.length) {
    return null;
  }
  return path;
}

export async function convertToDisplayObject(arr) {
  if (!arr) return [];
  const results: documentCard[] = await Promise.all(
    arr.map(async item => {
      const card = new documentCard();
      card.id = item.docId;
      card.author = item.writer ? item.writer : getHostName(item?.url);
      card.type = 'document';
      card.title = item.title;
      card.url = item.url;
      card.content = item.description;
      card.keyword = item?.keyword;
      if (item.tags?.length > 0 && item.tags[0].length > 0 && item.tags[0] != 'null') {
        card.tags = [...item.tags];
      }
      if (item.published_at) card.addedAt = new Date(item.published_at);
      card.modifiedAt = new Date(item.modify_date);
      card.totalDocuments = 1;
      card.favicon = item.favicon_url;
      card.search_type = item.search_type;
      if (item?.image_url?.[0] || item?.og_image_url || item?.og_image_base64) {
        card.images.push(item?.image_url?.[0] || item?.og_image_url || item?.og_image_base64 || '');
      } else {
        // @ts-ignore
        card.images = [detectFile(card.title, card.search_type)];
      }
      card.noConvertTime = false;
      return card;
    })
  );
  return results;
}

export function getHostName(url) {
  try {
    if (!url || url?.trim().length === 0) {
      return '';
    }

    if (Array.isArray(url)) {
      url = url[0];
    }

    if (checkIsDocFileByUrl(url)) {
      return 'File';
    }

    const hostname = new URL(url).hostname;
    const parts = hostname.split('.');

    // Remove common subdomains like www
    if (parts[0] === 'www') parts.shift();

    // Return the second-to-last part (main domain)
    return parts.length >= 2 ? parts.slice(0, parts.length - 1).join(' ') : hostname;
  } catch (e) {
    return null;
  }
}

export function isSocialDocument(document: any) {
  if (
    document.url.toString().includes('twitter.') ||
    document.url.toString().includes('.twitter') ||
    document.url.toString().includes('.facebook') ||
    document.url.toString().includes('facebook.') ||
    document.url.toString().includes('fb.com')
  ) {
    return true;
  }
  return false;
}

export function handleDateTimeCardFeed(datetime, $this?) {
  const current = new Date();
  // console.log('current', current);
  const previous = new Date(datetime);
  const msPerMinute = 60 * 1000;
  const msPerHour = msPerMinute * 60;
  const msPerDay = msPerHour * 24;
  const elapsed = current.getTime() - previous.getTime();
  const hours = Math.round(elapsed / msPerHour);
  // console.log('elapsed', elapsed);
  const date = Math.round(elapsed / msPerDay);
  // console.log('hours', hours);
  switch (true) {
    case elapsed <= 0: {
      return ` ${$this.$t('global.timeRecently')}`;
    }
    case Math.ceil(elapsed / msPerMinute) == 0: {
      return ` ${$this.$t('global.timeRecently')}`;
    }
    case hours == 0: {
      return ` ${$this.$t('global.timeRecently')}`;
    }
    case date == 0 && hours < 24: {
      return hours + ` ${$this.$t('global.timeHoursAgo')}`;
    }
    // case date > 0 && date < 7: {
    //   return `${date} ${$this.$t('global.timeDay')}`;
    // }
    // case date == 7: {
    //   return `1 ${$this.$t('global.timeWeek')}`;
    // }

    default:
      return previous.toLocaleDateString('en-US', {
        timeZone: 'UTC',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
  }
}

export function detectLanguageFromBrowser() {
  const language = window.navigator.language;
  if (language) {
    switch (language.substring(0, 2)) {
      case 'en':
        return 'en';
      case 'ko':
        return 'ko';
      default:
        return 'en';
    }
  }
}

export function removeDocSameUrl(arr: string[]) {
  const uniqueIds = new Set();

  const unique = arr.filter(element => {
    const urlHash = element.split('-')[4];
    const isDuplicate = uniqueIds.has(urlHash);
    uniqueIds.add(urlHash);
    if (!isDuplicate) {
      return true;
    }
    return false;
  });

  return unique;
}

export function noNeedToCraw(feed) {
  if (feed?.url) {
    return (
      feed.url.toLowerCase().includes('//facebook') ||
      feed.url.toLowerCase().includes('.facebook') ||
      feed.url.toLowerCase().includes('//twitter') ||
      feed.url.toLowerCase().includes('.twitter') ||
      feed.url.toLowerCase().includes('//linkedin') ||
      feed.url.toLowerCase().includes('.linkedin') ||
      feed.url.toLowerCase().includes('//instagram') ||
      feed.url.toLowerCase().includes('.instagram')
    );
  } else {
    return false;
  }
}

export function isNullish(object: any): boolean {
  if (!object) return true;
  if (Object.keys(object).length === 0) return true;
  return Object.values(object).every(value => !value);
}

export function convertToBriefingResourceReq(
  element: any,
  userKey: string,
  page: string,
  getSelectedBriefingPage: any,
  requestedResourceList: Array<any>,
  lang: string,
  lastName?: string,
  tempRequestIdInput?: any,
  isUpdateBriefing?: boolean,
  isNotInsertBpId?: boolean
) {
  const objectTmp: BriefingResourceRequest = {};
  objectTmp.requestId = tempRequestIdInput
    ? tempRequestIdInput
    : element.requestId
    ? element.requestId
    : Date.now() + Math.floor(Math.random() * 100);
  objectTmp.title = element.title ? element.title : '';
  objectTmp.forcedClustering = false;
  objectTmp.targetBpId =
    isNotInsertBpId !== undefined && isNotInsertBpId === true
      ? ''
      : (isUpdateBriefing ? element.targetBpId || (page === 'MyBriefing-Detail' && getSelectedBriefingPage?.bpId) || '' : '') || '';
  objectTmp.requestedResourceList = requestedResourceList
    ? requestedResourceList.map(item => {
        if (item.docId) {
          return item.docId;
        } else if (item.parent_doc_id && item.parent_doc_id.length > 0) {
          return item.parent_doc_id?.[0];
        }
      })
    : element.requestedResourceList;
  objectTmp.path = element.path ? element.path : element.url ? element.url : '';
  objectTmp.type = element.type ? element.type : '';
  objectTmp.collector = element.collector ? element.collector : '';
  objectTmp.userKey = userKey ? userKey : '';
  // objectTmp.author = element.author ? element.author : '';
  // objectTmp.writer = element.author ? [element.author] : element.writer ? element.writer : [''];
  objectTmp.writer = element.writer
    ? typeof element.writer === 'string'
      ? [element.writer]
      : element.writer
    : element.author
    ? [element.author]
    : [''];
  objectTmp.favicon = element.favicon ? element.favicon : '';
  objectTmp.lang = lang || 'en';
  objectTmp.keyword = element.keyword ? element.keyword : '';
  objectTmp.originDate = element.addedAt ? element.addedAt : element.originDate ? element.originDate : '';
  objectTmp.parentDocId = element.docId ? element.docId : element.parentDocId ? element.parentDocId : '';
  objectTmp.parent_doc_id = element.docId ? [element.docId] : element.parent_doc_id ? element.parent_doc_id : [''];
  objectTmp.image_uri = element.image_uri ? element.image_uri : [''];
  objectTmp.image_uri_thumbnail = element?.image_uri_thumbnail
    ? element?.image_uri_thumbnail
    : element?.originParentDocDetail?.image_uri_thumbnail
    ? element?.originParentDocDetail?.image_uri_thumbnail
    : element?.thumbnail
    ? element?.thumbnail
    : '';
  objectTmp.docType = 1;
  objectTmp.isGroup = element.isGroup ? element.isGroup : false;
  objectTmp.isInsideGroup = element.isInsideGroup ? element.isInsideGroup : false;
  objectTmp.search_type = element.searchType ? [element.searchType] : element.search_type ? element.search_type : ['WEB'];
  objectTmp.mediaType = element.mediaType ? element.mediaType : ['WEB'];
  objectTmp.channel = element.channel ? element.channel : '';
  if (
    (element.searchType && element.searchType === 'USERNOTE') ||
    (objectTmp.search_type && objectTmp.search_type.length > 0 && objectTmp.search_type?.[0] === 'USERNOTE')
  ) {
    objectTmp.channel = 'Note';
    objectTmp.mediaType = ['USERNOTE'];
  }
  objectTmp.published_at = element.published_at ? element.published_at : '';
  objectTmp.service_html = element.service_html ? element.service_html : '';
  if (objectTmp.type === 'USERNOTE') {
    objectTmp.content = element.content ? element.content : '';
    objectTmp.description = '';
    objectTmp.writer = lastName ? [lastName] : [''];
    objectTmp.search_type = ['USERNOTE'];
    objectTmp.mediaType = ['USERNOTE'];
    objectTmp.published_at = element.published_at || new Date().toISOString();
  } else {
    objectTmp.content = element.content ? element.content : '';
    objectTmp.description = element.description ? element.description : element.content ? element.content : '';
  }
  if (objectTmp.type === 'FILE' && element.docId) {
    objectTmp.search_type = ['FILE'];
    objectTmp.mediaType = [generateMediaType(element.docId, objectTmp.mediaType?.[0])];
    objectTmp.published_at = element.published_at || new Date().toISOString();
    objectTmp.writer = lastName ? [lastName] : [''];
    objectTmp.originDate = element.published_at || new Date().toISOString();
  }
  if ((objectTmp.search_type?.[0] === 'REPORT' && objectTmp.mediaType?.[0] === 'REPORT') || objectTmp.docType === 4) {
    objectTmp.originType = 'REPORT';
  } else {
    objectTmp.originType = 'DISCOVERY';
  }

  objectTmp.owner_user_key = element?.owner_user_key
    ? element?.owner_user_key
    : element?.originParentDocDetail?.owner_user_key
    ? element?.originParentDocDetail?.owner_user_key
    : userKey;

  // if (!objectTmp.requestId) {
  //   objectTmp.requestId = Date.now() + Math.floor(Math.random() * 100);
  // }
  // console.log(objectTmp);
  // objectTmp.parent_doc_id = element.docId
  //   ? [element.docId]
  //   : element.parent_doc_id
  //   ? element.parent_doc_id
  //   : element.parentDocId
  //   ? [element.parentDocId]
  //   : [''];
  // objectTmp.img = element.img ? element.img : element?.images?.[0] ? element.images[0] : '';

  return objectTmp;
}

export function convertToBriefingReferenceItem(document) {
  const briefingReferenceItem: any = {};

  briefingReferenceItem.parentDocId = document.parentDocId;
  briefingReferenceItem.docId = document.parentDocId;
  briefingReferenceItem.userKey = document.userKey;
  briefingReferenceItem.title = document.title;
  briefingReferenceItem.targetBpId = document.targetBpId;
  briefingReferenceItem.author = document?.writer?.[0] || '';
  // briefingReferenceItem.sourceUriDomain = document.path ? new URL(document.path).host : '';
  briefingReferenceItem.originDate = document.originDate || '';
  briefingReferenceItem.description = document.description;
  briefingReferenceItem.mediaType = document.mediaType;
  briefingReferenceItem.type = document.type;
  briefingReferenceItem.bpId = document.targetBpId;
  briefingReferenceItem.search_type = document.search_type;
  briefingReferenceItem.path = document.path;
  briefingReferenceItem.referenceDocumentIndex = document.referenceDocumentIndex > -1 ? document.referenceDocumentIndex : undefined;
  briefingReferenceItem.channel = document.channel || '';

  return briefingReferenceItem;
}

export function convertToBriefingReferenceItemRefactor(document, isNew?: boolean) {
  const briefingReferenceItem: any = {};

  // console.log('doc before: ', document);

  briefingReferenceItem.title = document.title || '';
  briefingReferenceItem.forcedClustering = document.forcedClustering || false;
  briefingReferenceItem.targetBpId = document.targetBpId || '';
  briefingReferenceItem.path = document.path || '';
  briefingReferenceItem.type = document.type || '';
  briefingReferenceItem.collector = document.collector || '';
  briefingReferenceItem.userKey = document.userKey || '';
  briefingReferenceItem.writer = document.writer || [''];
  briefingReferenceItem.favicon = document.favicon || '';
  briefingReferenceItem.lang = document.lang || 'en';
  briefingReferenceItem.keyword = document.keyword || '';
  briefingReferenceItem.originDate = document.originDate || '';
  briefingReferenceItem.parentDocId = document.parentDocId || '';
  briefingReferenceItem.parent_doc_id = document.parent_doc_id || [''];
  briefingReferenceItem.image_uri = document.image_uri || [''];
  briefingReferenceItem.image_uri_thumbnail = document.image_uri_thumbnail || '';
  briefingReferenceItem.docType = document.docType || 1;
  briefingReferenceItem.isGroup = document.isGroup || false;
  briefingReferenceItem.isInsideGroup = document.isInsideGroup || false;
  briefingReferenceItem.search_type = document.search_type || ['WEB'];
  briefingReferenceItem.mediaType = document.mediaType || ['WEB'];
  briefingReferenceItem.docId = document.parentDocId || '';
  briefingReferenceItem.userKey = document.userKey || '';
  briefingReferenceItem.channel = document.channel || '';
  briefingReferenceItem.author = document.author || '';
  briefingReferenceItem.description = document.description || '';
  briefingReferenceItem.type = document.type || '';
  briefingReferenceItem.bpId = document.targetBpId || '';
  briefingReferenceItem.published_at = document.published_at || '';
  briefingReferenceItem.service_html = document.service_html || '';
  briefingReferenceItem.content = document.content || '';
  briefingReferenceItem.referenceDocumentIndex = document.referenceDocumentIndex > -1 ? document.referenceDocumentIndex : undefined;
  briefingReferenceItem.owner_user_key = document.owner_user_key || '';

  if (isNew !== undefined && isNew === true) {
    briefingReferenceItem.isNew = true;
  }

  // console.log('briefingReferenceItem: ', briefingReferenceItem);

  return briefingReferenceItem;
}

export function checkIfIsBase64Image(dataInput: any) {
  return dataInput.includes(';base64') && dataInput.includes('data:image');
}

function generateMediaType(docId: string, mediaTypeInput: string) {
  const tempMediaTypeInput = mediaTypeInput.toUpperCase();

  if (tempMediaTypeInput.includes('DOC')) {
    return 'DOC';
  }

  if (tempMediaTypeInput.includes('PPT')) {
    return 'PPT';
  }

  if (tempMediaTypeInput.includes('PDF')) {
    return 'PDF';
  }

  if (docId.includes('pdf')) {
    return 'PDF';
  }
  if (docId.includes('doc') || docId.includes('docx')) {
    return 'DOC';
  }
  if (docId.includes('xls') || docId.includes('xlsx')) {
    return 'XLS';
  }
  if (docId.includes('jpg')) {
    return 'JPG';
  }
  if (docId.includes('jpeg')) {
    return 'JPEG';
  }
  if (docId.includes('png')) {
    return 'PNG';
  }
  if (docId.includes('txt')) {
    return 'TXT';
  }
  if (docId.includes('zip')) {
    return 'ZIP';
  }
  if (docId.includes('ppt') || docId.includes('pptx')) {
    return 'PPT';
  }

  return 'WEB';
}

export function showReponseMessage(mess, title, variant, hideDelay, bvToast?) {
  bvToast.toast(mess, {
    toaster: 'b-toaster-bottom-right',
    title: title,
    variant: variant,
    solid: true,
    autoHideDelay: hideDelay,
  });
}

export function mergeTwoArrayByRemoveAllDuplicatedItemByDocIdAndPath(targetArray: any[], addingArray: any[]) {
  try {
    const tempMapByParentDocId = new Map<string, BriefingResourceRequest>();
    const tempMapByPath = new Map<string, BriefingResourceRequest>();
    let tempItem;
    let countRemove = 0;

    targetArray.forEach(item => {
      tempMapByParentDocId.set(`${item.parentDocId}`, item);
    });

    // console.log('tempMapByParentDocId.size: ', tempMapByParentDocId.size);

    addingArray.forEach(item => {
      if (tempMapByParentDocId.has(`${item.parentDocId}`)) {
        if (
          !tempMapByParentDocId.get(`${item.parentDocId}`)['chunk_index'] &&
          !tempMapByParentDocId.get(`${item.parentDocId}`)['sourceUriDomain']
        ) {
          tempItem = {
            ...item,
            referenceDocumentIndex: tempMapByParentDocId.get(`${item.parentDocId}`)['referenceDocumentIndex'],
          };
          tempMapByParentDocId.set(`${item.parentDocId}`, tempItem);
        }

        countRemove = countRemove + 1;
      } else {
        tempMapByParentDocId.set(`${item.parentDocId}`, {
          ...item,
          referenceDocumentIndex: item.referenceDocumentIndex - countRemove,
        });
      }
    });

    countRemove = 0;

    // console.log('tempMapByParentDocId.size: ', tempMapByParentDocId.size);

    tempMapByParentDocId.forEach(item => {
      if (item.path && item.path !== '' && item.mediaType && item.mediaType?.[0] !== 'USERNOTE') {
        if (tempMapByPath.has(`${item.path}`)) {
          if (!tempMapByPath.get(`${item.path}`)['chunk_index'] && !tempMapByPath.get(`${item.path}`)['sourceUriDomain']) {
            tempItem = {
              ...item,
              referenceDocumentIndex: tempMapByPath.get(`${item.path}`)['referenceDocumentIndex'],
            };
            tempMapByPath.set(`${item.path}`, tempItem);
          }

          countRemove = countRemove + 1;
        } else {
          tempItem = {
            ...item,
            referenceDocumentIndex: item['referenceDocumentIndex'] - countRemove,
          };
          tempMapByPath.set(`${item.path}`, tempItem);
        }
      } else {
        tempItem = {
          ...item,
          referenceDocumentIndex: item['referenceDocumentIndex'] - countRemove,
        };
        tempMapByPath.set(`${item.parentDocId}`, tempItem);
      }
    });

    // console.log('tempMapByPath.size: ', tempMapByPath.size);

    return Array.from(tempMapByPath.values());
  } catch (e) {
    // console.log('eeeeeeeeeeeeeeeeeeeeeee: ', e);
    return [...targetArray, ...addingArray];
  }
}

export function removeAllDuplicatedNote(currenListCheck: any[], inputList: any[]): any[] {
  // console.log(currenListCheck);
  // console.log(inputList);
  try {
    const resultList = [];

    inputList.forEach(inputItem => {
      let isInsert = true;

      if ((!inputItem.path || inputItem.path === '') && inputItem.mediaType?.[0] === 'USERNOTE') {
        for (const currentItem of currenListCheck) {
          if ((!currentItem.path || currentItem.path === '') && currentItem.mediaType?.[0] === 'USERNOTE') {
            if (currentItem.parentDocId === inputItem.parentDocId) {
              isInsert = false;
              break;
            }
          }
        }
      }

      if (isInsert) {
        resultList.push(inputItem);
      }
    });

    return resultList;
  } catch (e) {
    return inputList;
  }
}

// export function removeAllDuplicatedItemByDocIdAndPath(targetArray: any[]): any[] {
//   const tempMapByParentDocId = new Map<string, BriefingResourceRequest>();
//   const tempMapByPath = new Map<string, BriefingResourceRequest>();
//
//   targetArray.forEach(item => {
//     tempMapByParentDocId.set(`${item.parentDocId}`, item);
//   });
//
//   tempMapByParentDocId.forEach(item => {
//     tempMapByPath.set(`${item.path}`, item);
//   });
//
//   return Array.from(tempMapByPath.values());
// }

export function getValidURL(inputUrl) {
  try {
    return new URL(inputUrl); // Try creating a URL directly
  } catch (e) {
    inputUrl;
  }
}

export function convertMarkdown2Html(content: string, isRemoveSpecialText = false) {
  // Init markdown it
  if (typeof content != 'string') {
    return;
  }
  const md = markdownit({
    html: true,
    xhtmlOut: true,
    breaks: true,
    linkify: true,
    typographer: true,
    quotes: '“”‘’',
  });
  // Remember the old renderer if overridden, or proxy to the default renderer.
  const defaultRender =
    md.renderer.rules.link_open ||
    function (tokens, idx, options, env, self) {
      return self.renderToken(tokens, idx, options);
    };

  md.renderer.rules.link_open = function (tokens, idx, options, env, self) {
    // Add a new `target` attribute, or replace the value of the existing one.
    tokens[idx].attrSet('target', '_blank');

    // Pass the token to the default renderer.
    return defaultRender(tokens, idx, options, env, self);
  };
  if (isRemoveSpecialText) {
    const refPattern = /◖\d+◗/;
    if (refPattern.test(content)) {
      content = content.replace(/[◖◗]/g, '') || '';
    }
  }
  return md.render(content);
}

export function getLinkEmbedYoutube(urlOrigin: string) {
  // Regular expression pattern to match YouTube video ID
  const regex = /(?:v=|\/)([a-zA-Z0-9_-]{11})/;

  // Use match to extract video ID
  const match = urlOrigin.match(regex);

  // Check if match found
  if (match) {
    return `https://www.youtube.com/embed/${match[1]}`; // Extract the captured group
  }
  return urlOrigin;
}

export function sortBriefingPages(listFilter: IBriefingPage[]) {
  return listFilter.sort((a: IBriefingPage, b: IBriefingPage) => {
    return new Date(b.modifyDate).getTime() - new Date(a.modifyDate).getTime();
  });
}

export function sortPinnedBriefing(listFilter: IBriefingPage[]) {
  return listFilter.sort((a: IBriefingPage, b: IBriefingPage) => {
    return new Date(b.pinAt).getTime() - new Date(a.pinAt).getTime();
  });
}

export function sortScrapPin(listFilter: IScrapAgent[]) {
  // Separate pinned and unpinned pages
  const pinnedPages = listFilter.filter(page => page.isPinned);
  const unpinnedPages = listFilter.filter(page => !page.isPinned);

  // Add the new page to the beginning of the unpinned pages
  const updatedUnpinnedPages = unpinnedPages.sort((a, b) => new Date(b.pinAt).getTime() - new Date(a.pinAt).getTime());
  const updatedPinnedPages = pinnedPages.sort((a, b) => new Date(b.pinAt).getTime() - new Date(a.pinAt).getTime());
  return [...updatedPinnedPages, ...updatedUnpinnedPages];
}

export function getBase64(url, callback) {
  const xhr = new XMLHttpRequest();
  xhr.onload = function () {
    const reader = new FileReader();
    reader.onloadend = function () {
      callback(reader.result);
    };
    reader.readAsDataURL(xhr.response);
  };
  xhr.open('GET', url);
  xhr.responseType = 'blob';
  xhr.send();
}

export function checkIsDocFileByUrl(urlInput: string): boolean {
  let isDocFile = false;

  if (
    urlInput &&
    urlInput?.trim().length > 0 &&
    urlInput?.includes('UID_') &&
    urlInput?.includes('T') &&
    urlInput?.includes('Z_') &&
    urlInput?.includes('.') &&
    urlInput?.includes('.doc')
  ) {
    isDocFile = true;
  }

  return isDocFile;
}

export function isOutOfDateMakeAThon() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const makeAThonDueDate = DUE_DATE_MAKE_A_THON;
  const dueDate = new Date(makeAThonDueDate);
  return today <= dueDate;
}

export function getFileTypeAndImageByDocument(itemInput: any) {
  try {
    if (itemInput) {
      const tempUrl = itemInput.originUrl || '';
      let tempMediaType =
        itemInput.media_type ||
        itemInput.mediaType ||
        itemInput.originParentDocDetail?.media_type ||
        itemInput.originParentDocDetail?.mediaType ||
        '';

      if (tempMediaType !== undefined && tempMediaType !== '' && tempMediaType !== null) {
        if (Array.isArray(tempMediaType)) {
          tempMediaType = tempMediaType?.filter(item => {
            const tempString = item?.trim()?.toLowerCase() || '';

            if (tempString.length > 0) {
              return (
                tempString.includes(FILE_TYPES_ENUM.DOC.toString()) ||
                tempString.includes(FILE_TYPES_ENUM.PPT.toString()) ||
                tempString.includes(FILE_TYPES_ENUM.PDF.toString())
              );
            }

            return false;
          });
        } else {
          tempMediaType = tempMediaType.toLowerCase();
        }
      }

      const result = { fileType: '', fileCardImage: '' };
      const DOT_PDF = FILE_TYPES_ENUM.DOT_PDF.toString();
      const PDF = FILE_TYPES_ENUM.PDF.toString();

      if ((tempUrl?.includes(DOT_PDF) || tempMediaType?.includes(PDF)) && (tempUrl?.endsWith(DOT_PDF) || tempMediaType?.endsWith(PDF))) {
        result.fileType = REFERENCE_FILE_TYPE.PDF;
        result.fileCardImage = SVG_FILE.PDF.toString();
      }

      const DOT_DOC = FILE_TYPES_ENUM.DOT_DOC.toString();
      const DOC = FILE_TYPES_ENUM.DOC.toString();
      const DOCX = FILE_TYPES_ENUM.DOCX.toString();
      const DOT_DOCX = FILE_TYPES_ENUM.DOT_DOCX.toString();

      if (
        (tempUrl?.includes(DOT_DOC) || tempMediaType?.includes(DOC)) &&
        (tempUrl?.endsWith(DOT_DOC) || tempUrl?.endsWith(DOT_DOCX) || tempMediaType?.endsWith(DOC) || tempMediaType?.endsWith(DOCX))
      ) {
        result.fileType = REFERENCE_FILE_TYPE.DOC;
        result.fileCardImage = SVG_FILE.DOC.toString();
      }

      const DOT_PPT = FILE_TYPES_ENUM.DOT_PPT.toString();
      const PPT = FILE_TYPES_ENUM.PPT.toString();
      const PPTX = FILE_TYPES_ENUM.PPTX.toString();
      const DOT_PPTX = FILE_TYPES_ENUM.DOT_PPTX.toString();

      if (
        (tempUrl?.includes(DOT_PPT) || tempMediaType?.includes(PPT)) &&
        (tempUrl?.endsWith(DOT_PPT) || tempUrl?.endsWith(DOT_PPTX) || tempMediaType?.endsWith(PPT) || tempMediaType?.endsWith(PPTX))
      ) {
        result.fileType = REFERENCE_FILE_TYPE.PPT;
        result.fileCardImage = SVG_FILE.PPT.toString();
      }

      return result;
    } else {
      return null;
    }
  } catch (e) {
    return null;
  }
}

function userIdentity() {
  return store.getters?.account
    ? store.getters?.account
    : store.getters?.userIdentity
    ? store.getters?.userIdentity
    : JSON.parse(localStorage.getItem('localStorageUser'))
    ? JSON.parse(localStorage.getItem('localStorageUser'))
    : null;
}

export function getSavedUserKey() {
  return userIdentity()?.id || store.getters?.account?.id || JSON.parse(localStorage.getItem('localStorageUser'))?.id || null;
}
